import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, MessageCircle, Instagram, Music2, Youtube, Send, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

const SUBJECTS = ['Suporte', 'Financeiro', 'Parcerias', 'Bug Report', 'Outro'];

interface ChannelConfig {
  whatsapp: string;
  instagram: string;
  tiktok: string;
  youtube: string;
  emailSupport: string;
}

async function loadChannels(): Promise<ChannelConfig> {
  const { data } = await supabase
    .from('platform_settings')
    .select('key, value')
    .in('key', ['channel_whatsapp', 'channel_instagram', 'channel_tiktok', 'channel_youtube', 'channel_email_support']);

  const map: Record<string, string> = {};
  (data ?? []).forEach(r => { map[r.key] = String(r.value ?? ''); });

  return {
    whatsapp:     map['channel_whatsapp']     ?? '',
    instagram:    map['channel_instagram']    ?? 'professya',
    tiktok:       map['channel_tiktok']       ?? 'professya',
    youtube:      map['channel_youtube']      ?? '',
    emailSupport: map['channel_email_support'] ?? 'suporte@professya.com',
  };
}

export function ContactPage() {
  const [channels,    setChannels]    = useState<ChannelConfig | null>(null);
  const [name,        setName]        = useState('');
  const [email,       setEmail]       = useState('');
  const [subject,     setSubject]     = useState('Suporte');
  const [message,     setMessage]     = useState('');
  const [submitting,  setSubmitting]  = useState(false);
  const [submitted,   setSubmitted]   = useState(false);
  const [error,       setError]       = useState('');

  useEffect(() => {
    loadChannels().then(setChannels);
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setSubmitting(true);

    const { data } = await supabase.functions.invoke('send-contact-form', {
      body: { name, email, subject, message },
    });

    setSubmitting(false);
    if (data?.ok) {
      setSubmitted(true);
    } else {
      setError(data?.error ?? 'Erro ao enviar. Tente novamente ou nos escreva diretamente por email.');
    }
  }

  const channelCards = channels ? [
    channels.emailSupport && {
      icon: <Mail size={22} />,
      label: 'Email',
      value: channels.emailSupport,
      href: `mailto:${channels.emailSupport}`,
      color: '#60a5fa',
      bg: 'rgba(96,165,250,0.08)',
    },
    channels.whatsapp && {
      icon: <MessageCircle size={22} />,
      label: 'WhatsApp',
      value: 'Iniciar conversa',
      href: `https://wa.me/${channels.whatsapp}`,
      color: '#4ade80',
      bg: 'rgba(74,222,128,0.08)',
    },
    channels.instagram && {
      icon: <Instagram size={22} />,
      label: 'Instagram',
      value: `@${channels.instagram}`,
      href: `https://instagram.com/${channels.instagram}`,
      color: '#f472b6',
      bg: 'rgba(244,114,182,0.08)',
    },
    channels.tiktok && {
      icon: <Music2 size={22} />,
      label: 'TikTok',
      value: `@${channels.tiktok}`,
      href: `https://tiktok.com/@${channels.tiktok}`,
      color: '#a78bfa',
      bg: 'rgba(167,139,250,0.08)',
    },
    channels.youtube && {
      icon: <Youtube size={22} />,
      label: 'YouTube',
      value: 'Canal Professya',
      href: channels.youtube,
      color: '#f87171',
      bg: 'rgba(248,113,113,0.08)',
    },
  ].filter(Boolean) : [];

  return (
    <>
      <Helmet>
        <title>Fale Conosco, Professya</title>
        <meta name="description" content="Entre em contato com a equipe Professya. Suporte, parcerias e dúvidas." />
      </Helmet>

      <div style={{ maxWidth: '760px', margin: '0 auto', padding: '48px 24px 80px' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <p style={{ fontSize: '13px', color: '#D4A017', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '12px' }}>
            ✦ Contato
          </p>
          <h1 style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: 'clamp(2rem, 5vw, 2.8rem)',
            color: '#F5F0E8',
            marginBottom: '16px',
          }}>
            Fale Conosco
          </h1>
          <p style={{ color: '#8A7F6E', fontSize: '16px', lineHeight: 1.7, maxWidth: '480px', margin: '0 auto' }}>
            Nossa equipe está aqui para ajudar. Escolha o canal que preferir ou preencha o formulário abaixo.
          </p>
        </div>

        {/* Channel cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: '12px',
          marginBottom: '48px',
        }}>
          {channelCards.map((ch) => {
            if (!ch) return null;
            return (
              <a
                key={ch.label}
                href={ch.href}
                target={ch.href.startsWith('mailto') ? undefined : '_blank'}
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '20px 16px',
                  backgroundColor: ch.bg,
                  border: `1px solid ${ch.color}28`,
                  borderRadius: '12px',
                  textDecoration: 'none',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  cursor: 'pointer',
                }}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
                  (e.currentTarget as HTMLElement).style.boxShadow = `0 4px 16px ${ch.color}20`;
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
                  (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                }}
              >
                <span style={{ color: ch.color }}>{ch.icon}</span>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontWeight: 600, color: '#F5F0E8', fontSize: '14px', margin: '0 0 2px' }}>{ch.label}</p>
                  <p style={{ color: '#8A7F6E', fontSize: '12px', margin: 0 }}>{ch.value}</p>
                </div>
              </a>
            );
          })}
        </div>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '40px' }}>
          <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(31,78,95,0.15)' }} />
          <span style={{ color: '#5A5347', fontSize: '13px', whiteSpace: 'nowrap' }}>ou envie uma mensagem</span>
          <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(31,78,95,0.15)' }} />
        </div>

        {/* Contact form */}
        {submitted ? (
          <div style={{
            textAlign: 'center', padding: '48px 24px',
            backgroundColor: 'rgba(74,222,128,0.06)',
            border: '1px solid rgba(74,222,128,0.2)',
            borderRadius: '16px',
          }}>
            <CheckCircle size={48} color="#4ade80" style={{ margin: '0 auto 16px', display: 'block' }} />
            <h2 style={{ fontFamily: 'Playfair Display, serif', color: '#F5F0E8', fontSize: '1.5rem', marginBottom: '12px' }}>
              Mensagem enviada!
            </h2>
            <p style={{ color: '#8A7F6E', fontSize: '15px', lineHeight: 1.7 }}>
              Obrigado por entrar em contato. Nossa equipe irá responder em até 24–48 horas úteis.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', color: '#8A7F6E', letterSpacing: '0.05em' }}>Nome</label>
                <input
                  type="text"
                  className="form-input"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Seu nome"
                  required
                  minLength={2}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', color: '#8A7F6E', letterSpacing: '0.05em' }}>Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '13px', color: '#8A7F6E', letterSpacing: '0.05em' }}>Assunto</label>
              <select
                className="form-select"
                value={subject}
                onChange={e => setSubject(e.target.value)}
              >
                {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '13px', color: '#8A7F6E', letterSpacing: '0.05em' }}>Mensagem</label>
              <textarea
                className="form-input"
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Como podemos ajudar?"
                required
                minLength={10}
                rows={5}
                style={{ resize: 'vertical', minHeight: '120px' }}
              />
            </div>

            {error && (
              <p style={{ color: '#f87171', fontSize: '14px', padding: '12px 16px', backgroundColor: 'rgba(239,68,68,0.08)', borderRadius: '8px' }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting || !name || !email || !message}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                padding: '14px 32px',
                backgroundColor: '#D4A017',
                color: '#0C0B0F',
                fontWeight: 700,
                fontSize: '15px',
                border: 'none',
                borderRadius: '8px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1,
                alignSelf: 'flex-start',
              }}
            >
              <Send size={16} />
              {submitting ? 'Enviando...' : 'Enviar mensagem'}
            </button>
          </form>
        )}
      </div>
    </>
  );
}
